import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { ZONE_MATCH, money } from "@/lib/technicians";

const RateInfo = ({ rate }) => {
  if (!rate) return null;
  if (rate.status === "ok") return <span data-testid="candidate-rate">Tarifa interna: {money(rate.rate.amount, rate.rate.currency)}</span>;
  if (rate.status === "ambiguous") return <span className="text-amber-400" data-testid="candidate-rate">Varias tarifas aplicables ({rate.options.length})</span>;
  return <span data-testid="candidate-rate">Sin tarifa configurada</span>;
};

const Card = ({ c, selected, onPick }) => {
  const star = c.compatible_specialties.some((s) => s.kind === "unit");
  const zm = c.zone_match;
  return (
    <button type="button" onClick={onPick} data-testid={`candidate-${c.id}`}
      className={cn("w-full rounded-lg border px-3 py-2.5 text-left transition-colors", selected ? "border-primary bg-primary/5" : "border-border hover:border-primary/40")}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-medium">{c.name}</span>{star && <Star className="h-3.5 w-3.5 fill-primary text-primary" data-testid={`candidate-star-${c.id}`} />}
        <span className="rounded border border-border px-1.5 py-0.5 font-mono text-[10px] uppercase">{c.type_label}</span>
        {c.company && <span className="text-xs text-muted-foreground">{c.company}</span>}
        <span className="ml-auto font-mono text-xs text-muted-foreground" data-testid={`candidate-load-${c.id}`}>{c.workload.abiertas} OS abiertas</span>
      </div>
      <div className="mt-1 grid gap-x-4 gap-y-0.5 text-xs text-muted-foreground sm:grid-cols-2">
        <span>Especialidad: {c.compatible_specialties.length ? c.compatible_specialties.map((s) => s.name).join(", ") : "sin coincidencia"}</span>
        <span className={zm === "ninguna" ? "text-amber-400" : zm ? "text-emerald-400" : ""} data-testid={`candidate-zone-${c.id}`}>
          {zm ? ZONE_MATCH[zm] : "OS sin zona"}{c.zones.length ? ` · ${c.zones.join(", ")}` : " · sin zonas"}
        </span>
        <span className={c.availability.ok ? "text-emerald-400" : "text-red-400"} data-testid={`candidate-availability-${c.id}`}>{c.availability.label}</span>
        <span>{c.workload.en_proceso} en proceso · {c.workload.pendiente_cierre} pend. cierre · {c.workload.programadas} programadas</span>
        <RateInfo rate={c.rate} />
      </div>
    </button>
  );
};

export const TechnicianPicker = ({ query, value, specialty, rateId, currentName, onChange }) => {
  const [list, setList] = useState([]);
  const { business_unit_code, service_type_code, zone_id, scheduled_date, scheduled_time } = query;
  useEffect(() => {
    api.get("/technicians/candidates", { params: { business_unit_code: business_unit_code || undefined, service_type_code: service_type_code || undefined,
      zone_id: zone_id || undefined, scheduled_date: scheduled_date || undefined, scheduled_time: scheduled_time || undefined } }).then((r) => setList(r.data));
  }, [business_unit_code, service_type_code, zone_id, scheduled_date, scheduled_time]);
  const sel = list.find((c) => c.id === value);
  const pick = (c) => onChange({ technician_id: c?.id || null, specialty_code: c?.compatible_specialties.length === 1 ? c.compatible_specialties[0].code : null, technician_rate_id: null });
  return (
    <div className="space-y-2" data-testid="technician-picker">
      <p className="text-xs text-muted-foreground">Información de apoyo: la decisión final es de Admin/Supervisor. La lista no está ordenada por recomendación.</p>
      <button type="button" onClick={() => pick(null)} data-testid="candidate-none"
        className={cn("w-full rounded-lg border px-3 py-2 text-left text-sm", !value ? "border-primary bg-primary/5" : "border-border")}>Sin asignar</button>
      {value && !sel && <div className="rounded-lg border border-primary bg-primary/5 px-3 py-2 text-sm" data-testid="candidate-current">{currentName || "Técnico actual"} <span className="text-xs text-muted-foreground">(actual · no disponible como candidato nuevo)</span></div>}
      <div className="max-h-[420px] space-y-2 overflow-y-auto">{list.map((c) => <Card key={c.id} c={c} selected={c.id === value} onPick={() => pick(c)} />)}</div>
      {sel && sel.compatible_specialties.length > 1 && (
        <div className="space-y-1" data-testid="assign-specialty-block">
          <span className="text-xs text-amber-400">Varias especialidades compatibles: selecciona con cuál se asigna</span>
          <Select value={specialty || undefined} onValueChange={(v) => onChange({ technician_id: value, specialty_code: v, technician_rate_id: rateId })}>
            <SelectTrigger data-testid="assign-specialty-select"><SelectValue placeholder="Seleccionar especialidad" /></SelectTrigger>
            <SelectContent>{sel.compatible_specialties.map((s) => <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      {sel?.rate?.status === "ambiguous" && (
        <div className="space-y-1" data-testid="assign-rate-block">
          <span className="text-xs text-amber-400">Hay varias tarifas con la misma prioridad: selecciona cuál aplicar</span>
          <Select value={rateId || undefined} onValueChange={(v) => onChange({ technician_id: value, specialty_code: specialty, technician_rate_id: v })}>
            <SelectTrigger data-testid="assign-rate-select"><SelectValue placeholder="Seleccionar tarifa" /></SelectTrigger>
            <SelectContent>{sel.rate.options.map((r) => <SelectItem key={r.id} value={r.id}>{money(r.amount, r.currency)} · v{r.version} · desde {r.valid_from}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
};
